import { useEffect, useRef, useState } from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import Section from '../components/Section';
import Seo from '../components/Seo';

const CALENDLY_SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_URL = 'https://calendly.com/lutfu-okman/30min?text_color=0b1f2a&primary_color=c59b42';

const BookConsultation = () => {
  const widgetRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const widgetNode = widgetRef.current;
    let observer = null;
    let timeoutId = null;
    let frameLoadHandler = null;

    const attachFrameListener = () => {
      const iframe = widgetNode?.querySelector('iframe');
      if (!iframe) {
        return false;
      }

      frameLoadHandler = () => {
        setIsLoading(false);
      };

      iframe.addEventListener('load', frameLoadHandler, { once: true });
      return true;
    };

    const setupLoadTracking = () => {
      if (!attachFrameListener() && widgetNode) {
        observer = new MutationObserver(() => {
          if (attachFrameListener() && observer) {
            observer.disconnect();
            observer = null;
          }
        });
        observer.observe(widgetNode, { childList: true, subtree: true });
      }

      timeoutId = window.setTimeout(() => {
        setIsLoading(false);
      }, 8000);
    };

    setIsLoading(true);
    const existingScript = document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
    if (existingScript) {
      setupLoadTracking();
      return () => {
        if (observer) observer.disconnect();
        if (timeoutId) window.clearTimeout(timeoutId);
      };
    }

    const script = document.createElement('script');
    script.src = CALENDLY_SCRIPT_SRC;
    script.async = true;
    script.addEventListener('load', setupLoadTracking, { once: true });
    document.body.appendChild(script);

    return () => {
      script.removeEventListener('load', setupLoadTracking);
      if (observer) observer.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
      const iframe = widgetNode?.querySelector('iframe');
      if (iframe && frameLoadHandler) {
        iframe.removeEventListener('load', frameLoadHandler);
      }
    };
  }, []);

  return (
    <>
      <Seo
        title="Book a Free Consultation | Fractional Sales VP for Industrial B2B Companies"
        description="Book a free consultation with SalesFractionPro."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Book a Free Consultation',
          description: 'Schedule a consultation with SalesFractionPro to discuss fractional sales leadership support.',
        }}
      />

      <Section className="page-hero" variant="hero">
        <Row>
          <Col>
            <h1 style={{ fontSize: 60 }}>Book a Free Consultation</h1>
          </Col>
        </Row>
      </Section>

      <Section variant="alt" className='py-0 overflow-hidden'>
        <Row>
          <Col>
            <div style={{ position: 'relative', minWidth: '320px', minHeight: '700px' }}>
              {isLoading ? (
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(244, 246, 248, 0.9)', zIndex: 1 }}
                >
                  <Spinner animation="border" role="status" aria-label="Loading booking calendar" />
                </div>
              ) : null}
              <div
                ref={widgetRef}
                className="calendly-inline-widget"
                data-url={CALENDLY_URL}
                style={{ minWidth: '320px', height: '700px' }}
              />
            </div>
          </Col>
        </Row>
      </Section>
    </>
  );
};

export default BookConsultation;
